import { STAGE_LABEL, type JobStage } from "@roof-hawk/core";

import { Stub } from "../../components/ui";
import { t } from "../../i18n";
import { fmtDateTime } from "../../lib/format";
import { useAsync } from "../../lib/hooks";
import { fetchJobActivity } from "../../lib/queries";

const KIND_LABEL: Record<string, string> = {
  done: "DONE",
  stage: "STAGE",
  note: "NOTE",
};

/** Checklist completions, stage changes and notes, newest first — with who. */
export function ActivityPanel({ jobId }: { jobId: string }) {
  const activity = useAsync(() => fetchJobActivity(jobId), [jobId]);
  const rows = activity.data ?? [];

  return (
    <div className="block">
      <h2>{t("Activity")}</h2>
      {rows.length === 0 ? (
        <Stub
          title={t("No activity yet")}
          message={t(
            "Completed steps, status changes, and notes will show here — with who and when.",
          )}
        />
      ) : (
        rows.map((row, i) => (
          <div className="note" key={`${row.when}-${i}`}>
            <div className={`ntype ${row.kind === "done" ? "call" : row.kind}`}>
              {t(KIND_LABEL[row.kind] ?? "NOTE")}
            </div>
            <div style={{ flex: 1 }}>
              <div className="nmeta">
                <span>{row.who ?? ""}</span>
                <span>·</span>
                <span>{fmtDateTime(row.when)}</span>
              </div>
              <div>{humanise(row.text)}</div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/** Stage rows arrive with the raw enum; show the label people recognise. */
function humanise(text: string): string {
  const match = /^Status → (.+)$/.exec(text);
  if (!match) return text;
  const stage = match[1] as JobStage;
  return t("Status → {stage}", { stage: t(STAGE_LABEL[stage] ?? stage) });
}
